import androidx.compose.runtime.*
import org.jetbrains.compose.web.dom.*

private val EVENT_KIND_LABELS = mapOf("tournament" to "Turnaj", "announcement" to "Vyhlášení", "meeting" to "Schůze", "other" to "Jiné")

/** One row in the merged chronological listing (spec 11.3): either a round slot or a freeform event. */
private data class ListingRow(
    val dateFrom: String,
    val dateTo: String?,
    val label: String,
    val linkUrl: String? = null,
    val note: String? = null,
)

private fun roundLabel(slot: SeasonRoundSlotDto, leagues: List<SeasonLeagueDto>): String {
    if (slot.leagueIds.isEmpty()) return "Kolo ${slot.roundNumber}"
    val names = slot.leagueIds.map { lid -> leagues.find { it.id == lid }?.name ?: "?" }
    return "Kolo ${slot.roundNumber} — vložené kolo jen pro: ${names.joinToString(", ")}"
}

@Composable
fun PublicTerminovaListinaScreen(season: SeasonDto?, roundSlots: List<SeasonRoundSlotDto>, leagues: List<SeasonLeagueDto>) {
    var events by remember { mutableStateOf<List<SeasonEventDto>>(emptyList()) }
    var loading by remember { mutableStateOf(true) }
    var error by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(season?.id) {
        val currentSeason = season ?: return@LaunchedEffect
        loading = true
        try {
            events = Api.fetchSeasonEvents(null, currentSeason.id)
            error = null
        } catch (e: Throwable) {
            error = "Načtení termínové listiny selhalo: ${e.apiMessage()}"
        }
        loading = false
    }

    Div(attrs = { classes("card") }) {
        if (season == null) {
            Text("Právě není žádná aktivní sezóna.")
            return@Div
        }

        if (loading) Text("Načítám…")
        error?.let { Div { Text("Chyba: $it") } }

        val rows = (
            roundSlots.map { ListingRow(it.dateFrom, it.dateTo, roundLabel(it, leagues)) } +
                events.map {
                    ListingRow(
                        it.dateFrom, it.dateTo,
                        "${it.title} (${EVENT_KIND_LABELS[it.kind] ?: it.kind})",
                        // Navázaný turnaj (spec 17.3) má přednost před ručně zadaným odkazem —
                        // vede na jeho popisnou stránku na subdoméně turnajů.
                        linkUrl = it.tournament?.let { t -> tournamentPublicUrl(t.slug) } ?: it.linkUrl,
                        note = it.note,
                    )
                }
            ).sortedBy { it.dateFrom }

        if (!loading && rows.isEmpty()) {
            Text("Zatím žádné termíny.")
        } else {
            Table(attrs = { classes("public-table") }) {
                Tr {
                    Th(attrs = { classes("date-col") }) { Text("Termín") }
                    Th { Text("Událost") }
                }
                rows.forEach { row -> key(row.dateFrom + row.label) { ListingRowView(row) } }
            }
        }
    }
}

@Composable
private fun ListingRowView(row: ListingRow) {
    var expanded by remember { mutableStateOf(false) }

    Tr {
        Td(attrs = { classes("date-col") }) { Text(dateRangeLabel(row.dateFrom, row.dateTo)) }
        Td {
            when {
                row.linkUrl != null -> A(href = row.linkUrl, attrs = { classes("event-title-linked") }) { Text(row.label) }
                row.note != null -> {
                    Span(attrs = {
                        classes("event-title-notable")
                        attr("title", row.note)
                        onClick { expanded = !expanded }
                    }) { Text(row.label) }
                    if (expanded) Div(attrs = { classes("event-note-expanded") }) { Text(row.note) }
                }
                else -> Text(row.label)
            }
        }
    }
}
